<script setup>
// Criação MANUAL de lead avulso (sem conversa) - acionado pela tela /leads.
// Espelha o CreateLeadDialog (intenção/classificação/pipeline/resumo), mas
// o contato é escolhido por busca em vez de vir de uma conversa.
// O backend (LeadsController#create) já qualifica e roteia ao pipeline.
import { ref, computed, watch } from 'vue';
import LeadsAPI from 'dashboard/api/leads';
import LeadIntentsAPI from 'dashboard/api/leadIntents';
import PipelinesAPI from 'dashboard/api/pipelines';
import ContactsAPI from 'dashboard/api/contacts';
import { useAlert } from 'dashboard/composables';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import SelectInput from 'dashboard/components-next/select/Select.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';

const emit = defineEmits(['created']);

const dialogRef = ref(null);
const isSubmitting = ref(false);
const intents = ref([]);
const pipelines = ref([]);

const contactQuery = ref('');
const contactResults = ref([]);
const selectedContact = ref(null);
const isSearching = ref(false);

const form = ref({
  lead_intent_id: null,
  classification: 'warm',
  pipeline_id: null,
  value_cents: '',
  summary: '',
});

const canSubmit = computed(
  () => Boolean(form.value.lead_intent_id) && Boolean(selectedContact.value?.id)
);

const intentOptions = computed(() => [
  { value: '', label: '- selecione -' },
  ...intents.value.map(i => ({ value: i.id, label: i.name })),
]);
const pipelineOptions = computed(() => [
  { value: '', label: 'Padrão do intent' },
  ...pipelines.value.map(p => ({ value: p.id, label: p.name })),
]);

async function loadOptions() {
  try {
    const [iRes, pRes] = await Promise.all([
      LeadIntentsAPI.get(),
      PipelinesAPI.get(),
    ]);
    intents.value = iRes?.data?.lead_intents || iRes?.data || [];
    pipelines.value = pRes?.data?.pipelines || pRes?.data || [];
  } catch {
    intents.value = [];
    pipelines.value = [];
  }
}

// Busca de contato com debounce (padrão da plataforma - sem ContactSelector,
// que é acoplado ao fluxo de nova conversa).
let searchTimer = null;
watch(contactQuery, q => {
  if (searchTimer) clearTimeout(searchTimer);
  if (!q || q.trim().length < 2 || selectedContact.value) {
    contactResults.value = [];
    return;
  }
  searchTimer = setTimeout(async () => {
    isSearching.value = true;
    try {
      const { data } = await ContactsAPI.search(q.trim(), 1);
      contactResults.value = data?.payload || data || [];
    } catch {
      contactResults.value = [];
    } finally {
      isSearching.value = false;
    }
  }, 250);
});

function pickContact(contact) {
  selectedContact.value = contact;
  contactQuery.value = contact.name || contact.email || `#${contact.id}`;
  contactResults.value = [];
}

function clearContact() {
  selectedContact.value = null;
  contactQuery.value = '';
  contactResults.value = [];
}

function resetForm() {
  form.value = {
    lead_intent_id: null,
    classification: 'warm',
    pipeline_id: null,
    value_cents: '',
    summary: '',
  };
  clearContact();
}

defineExpose({
  open() {
    resetForm();
    loadOptions();
    dialogRef.value?.open();
  },
  close() {
    dialogRef.value?.close();
  },
});

async function submit() {
  if (!canSubmit.value || isSubmitting.value) return;
  isSubmitting.value = true;
  try {
    const payload = {
      contact_id: Number(selectedContact.value.id),
      lead_intent_id: Number(form.value.lead_intent_id),
      classification: form.value.classification,
      summary: form.value.summary || null,
    };
    if (form.value.pipeline_id) payload.pipeline_id = Number(form.value.pipeline_id);
    if (form.value.value_cents) {
      const cents = Math.round(
        Number(String(form.value.value_cents).replace(',', '.')) * 100
      );
      if (!Number.isNaN(cents) && cents > 0) payload.value_cents = cents;
    }

    const { data: lead } = await LeadsAPI.create({ lead: payload });
    emit('created', lead);
    useAlert('Lead criado e roteado para o pipeline.');
    dialogRef.value?.close();
  } catch (e) {
    useAlert(
      e?.response?.data?.error ||
        e?.response?.data?.errors?.join?.(', ') ||
        'Falha ao criar lead.'
    );
  } finally {
    isSubmitting.value = false;
  }
}
</script>

<template>
  <Dialog
    ref="dialogRef"
    width="lg"
    :show-cancel-button="false"
    :show-confirm-button="false"
  >
    <div class="flex flex-col gap-4">
      <div>
        <h3 class="text-heading-3 text-n-slate-12 mb-1">Criar lead</h3>
        <p class="text-body-small text-n-slate-11 mb-0">
          Cadastro manual de lead. Selecione o contato e a intenção; o lead é roteado ao pipeline automaticamente.
        </p>
      </div>

      <!-- Seleção de contato -->
      <div class="relative">
        <label class="text-body-small text-n-slate-12 font-medium mb-1.5 block">
          Contato
        </label>
        <div v-if="selectedContact" class="flex items-center justify-between gap-2 rounded-lg border border-n-weak bg-n-alpha-1 px-3 py-2">
          <span class="text-body-main text-n-slate-12 truncate">
            {{ selectedContact.name || selectedContact.email || `#${selectedContact.id}` }}
          </span>
          <button
            type="button"
            class="text-n-slate-11 hover:text-n-slate-12 shrink-0"
            @click="clearContact"
          >
            <Icon icon="i-lucide-x" class="size-4" />
          </button>
        </div>
        <template v-else>
          <Input
            v-model="contactQuery"
            type="search"
            placeholder="Buscar contato por nome, e-mail ou telefone…"
          />
          <div
            v-if="contactResults.length"
            class="absolute z-10 mt-1 w-full max-h-56 overflow-y-auto rounded-lg border border-n-weak bg-n-solid-1 shadow-md"
          >
            <button
              v-for="c in contactResults"
              :key="c.id"
              type="button"
              class="w-full px-3 py-2 text-left hover:bg-n-alpha-2 flex flex-col"
              @click="pickContact(c)"
            >
              <span class="text-body-main text-n-slate-12 truncate">{{ c.name || '-' }}</span>
              <span class="text-label-small text-n-slate-10 truncate">
                {{ c.email || c.phone_number || `#${c.id}` }}
              </span>
            </button>
          </div>
        </template>
      </div>

      <div>
        <label class="text-body-small text-n-slate-12 font-medium mb-1.5 block">
          Intenção
        </label>
        <SelectInput v-model="form.lead_intent_id" :options="intentOptions" />
      </div>

      <div>
        <label class="text-body-small text-n-slate-12 font-medium mb-1.5 block">Classificação</label>
        <div class="flex gap-2">
          <button
            v-for="c in [
              { value: 'hot',  label: 'Quente', cls: 'border-n-ruby-9 bg-n-ruby-3 text-n-ruby-12' },
              { value: 'warm', label: 'Morno',  cls: 'border-n-amber-9 bg-n-amber-3 text-n-amber-12' },
              { value: 'cold', label: 'Frio',   cls: 'border-n-slate-7 bg-n-alpha-2 text-n-slate-11' },
            ]"
            :key="c.value"
            type="button"
            class="flex-1 px-3 py-1.5 rounded-md border text-body-small font-medium transition-colors"
            :class="form.classification === c.value ? c.cls : 'border-n-weak text-n-slate-11 hover:border-n-strong'"
            @click="form.classification = c.value"
          >
            {{ c.label }}
          </button>
        </div>
      </div>

      <div class="grid grid-cols-2 gap-3">
        <div class="flex flex-col gap-1">
          <label class="text-body-small text-n-slate-12 font-medium mb-1.5">
            Pipeline (opcional)
          </label>
          <SelectInput v-model="form.pipeline_id" :options="pipelineOptions" />
        </div>
        <div class="flex flex-col gap-1">
          <label class="text-body-small text-n-slate-12 font-medium mb-1.5">
            Valor estimado (R$)
          </label>
          <Input v-model="form.value_cents" placeholder="Ex: 1500,00" />
        </div>
      </div>

      <div>
        <label class="text-body-small text-n-slate-12 font-medium mb-1.5 block">Resumo</label>
        <textarea
          v-model="form.summary"
          rows="3"
          placeholder="Contexto do lead - o que o cliente quer, próximo passo."
          class="w-full rounded-lg border-none outline outline-1 outline-offset-[-1px] bg-n-alpha-black2 text-n-slate-12 placeholder:text-n-slate-10 outline-n-weak hover:outline-n-slate-6 focus:outline-n-brand transition-all duration-200 px-3 py-2.5 text-sm resize-none !mb-0"
        />
      </div>

      <div class="flex justify-end gap-2 pt-2">
        <NextButton faded slate label="Cancelar" @click="dialogRef?.close()" />
        <NextButton
          :is-loading="isSubmitting"
          :disabled="!canSubmit"
          label="Criar lead"
          icon="i-lucide-target"
          @click="submit"
        />
      </div>
    </div>
  </Dialog>
</template>
